import { useCallback, useMemo } from "react";
import {
  pickTodaySession,
  type PlanStarter,
  type TodaySession,
} from "@heraion/shared";
import {
  CreateHubStartBody,
  useExerciseNameBySlug,
  useLocale,
  usePlanGapsCoverage,
  type StartBuiltSessionInput,
  type UiTheme,
} from "@heraion/ui";

import { useSelfAthleteTargets } from "./progress/useSelfAthleteTargets";
import { useSelfProgressDeps } from "./progress/progressDeps";
import { useTodayRestHours } from "./progress/useTodayRestHours";
import { useFollowedPrograms } from "./useFollowedPrograms";
import { bodyweightOf, useShellProfile } from "./ThemeProfileBridge";
import { useWorkoutStarter } from "./useWorkoutStarter";

// Web mount of the shared CreateHubStartBody (AP-9b), mounted only while the
// hub is open. Data, platform theme, callbacks: the tree lives in
// @heraion/ui. A built starter goes to the logger through `onStarter`, the
// channel the library's Start already uses.
export function CreateHubStartWeb({
  theme,
  onNewWorkout,
  onPlanProgram,
  onPlanWorkout,
  onStarter,
}: {
  theme: UiTheme;
  onNewWorkout: () => void;
  onPlanProgram: () => void;
  onPlanWorkout: () => void;
  onStarter: (starter: PlanStarter) => void;
}) {
  const lang = useLocale();
  const base = useSelfProgressDeps();
  // The same targets web Progress passes, so the gaps here are the card's.
  const { value: athleteTargets } = useSelfAthleteTargets();
  const deps = useMemo(() => ({ ...base, athleteTargets }), [base, athleteTargets]);
  // #492 F5: the bodyweight toggle scopes the coverage the row builds from.
  const { coverage, catalog, bodyweightOnly, setBodyweightOnly } = usePlanGapsCoverage(deps);
  const { programsState } = useFollowedPrograms();
  const { doc } = useShellProfile();
  const pinnedId = doc?.["currentProgramId"] as string | undefined;
  const today = useMemo(
    () => pickTodaySession(programsState.followedPrograms, programsState.progress, lang, pinnedId),
    [programsState.followedPrograms, programsState.progress, lang, pinnedId],
  );
  const bodyweightKg = bodyweightOf(doc);
  const catalogList = useMemo(() => (catalog ? [...catalog] : null), [catalog]);
  const resolveName = useExerciseNameBySlug(catalog);
  const { starting, startProgramSession, startBuiltSession } = useWorkoutStarter(
    catalogList,
    bodyweightKg,
    onStarter,
  );
  // The rest the draft's chips save: the draft opens with it.
  const [restHours] = useTodayRestHours();
  const onBuildToday = useCallback(
    (input: StartBuiltSessionInput) => void startBuiltSession({ ...input, restHours }),
    [startBuiltSession, restHours],
  );
  const onStartToday = useCallback(
    (s: TodaySession) => void startProgramSession(s.program, s.weekIdx, s.sessionIdx),
    [startProgramSession],
  );

  return (
    <CreateHubStartBody
      theme={theme}
      today={today}
      coverage={coverage}
      catalog={catalog}
      painMap={deps.painMap}
      resolveName={resolveName}
      starting={starting}
      onNewWorkout={onNewWorkout}
      onPlanProgram={onPlanProgram}
      onPlanWorkout={onPlanWorkout}
      onStartToday={onStartToday}
      onBuildToday={onBuildToday}
      bodyweightOnly={bodyweightOnly}
      onBodyweightOnly={setBodyweightOnly}
    />
  );
}
